import { Button, CircularProgress } from '@material-ui/core';
import React, { useCallback, useRef, useState, VFC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import {
  createKnowledgeBase,
  triggerBootstrap,
  updateAxAgent,
  uploadFileSource,
} from 'containers/ChatAgent/api';
import { messages } from 'containers/ChatAgent/messages';
import { AxAgentValues } from 'containers/ChatAgent/types';
import { addMessageAction } from 'containers/FlashMessage/actions';

import {
  ACCEPTED_KB_FILE_EXTENSIONS,
  ACCEPTED_KB_FILE_EXTENSIONS_REGEX,
  ACCEPTED_KB_MIME_TYPES,
} from './constants';
import { FILE_ICON_SVG, UPLOAD_ILLUSTRATION_SVG } from './icons';
import { useUploadScreenStyles } from './styles';

export interface UploadScreenProps {
  accessToken: string;
  axAgentValues: AxAgentValues;
  setAxAgentValues: React.Dispatch<React.SetStateAction<AxAgentValues>>;
  onSkip: () => void;
  onUploadSuccess: (knowledgeBaseUuid: string) => void;
}

export const UploadScreen: VFC<UploadScreenProps> = ({
  accessToken,
  axAgentValues,
  setAxAgentValues,
  onSkip,
  onUploadSuccess,
}) => {
  const classes = useUploadScreenStyles();
  const intl = useIntl();
  const dispatch = useDispatch();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFiles = useCallback((incoming: FileList | File[]) => {
    const valid = Array.from(incoming).filter(
      f =>
        ACCEPTED_KB_MIME_TYPES.includes(f.type) ||
        (f.type === '' && ACCEPTED_KB_FILE_EXTENSIONS_REGEX.test(f.name)),
    );
    if (valid.length) {
      setSelectedFiles(prev => {
        const existing = new Set(prev.map(f => f.name));
        return [...prev, ...valid.filter(f => !existing.has(f.name))];
      });
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragActive(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const removeFile = (name: string) => {
    setSelectedFiles(prev => prev.filter(f => f.name !== name));
  };

  const handleUploadSubmit = async () => {
    if (!selectedFiles.length) return;
    setIsUploading(true);
    try {
      let kbUuid = axAgentValues.knowledgeBaseUuid;
      if (!kbUuid) {
        const kb = await createKnowledgeBase(accessToken, 'v1');
        kbUuid = kb.uuid;
        setAxAgentValues(prev => ({ ...prev, knowledgeBaseUuid: kbUuid }));
        await updateAxAgent(accessToken, axAgentValues.axAgentId, {
          knowledgeBaseUuid: kbUuid,
        });
      }
      // Sequential (not Promise.all): each file kicks off a heavy bootstrap job.
      let anyFailed = false;
      let anySucceeded = false;
      for (const file of selectedFiles) {
        try {
          await uploadFileSource(accessToken, kbUuid as string, file);
          await triggerBootstrap(
            accessToken,
            kbUuid as string,
            file,
            axAgentValues.axAgentId,
          );
          anySucceeded = true;
        } catch {
          anyFailed = true;
        }
      }
      setSelectedFiles([]);
      if (anyFailed) {
        dispatch(
          addMessageAction(
            intl.formatMessage(messages.uploadFileFailed),
            'error',
          ),
        );
      }
      if (anySucceeded) {
        onUploadSuccess(kbUuid);
      }
    } catch {
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.uploadFileFailed),
          'error',
        ),
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.contentSection}>
        <p className={classes.description}>
          Upload files like employee handbook, recruiter scripts, candidate
          FAQs, benefits guides, or hiring process overview &mdash; our AI will
          turn them into your agent&apos;s knowledge base automatically.
        </p>

        {/* Drop zone */}
        <div
          className={`${classes.dropZone} ${
            isDragActive ? classes.dropZoneActive : ''
          }`}
          onDrop={handleDrop}
          onDragOver={e => {
            e.preventDefault();
            setIsDragActive(true);
          }}
          onDragLeave={() => setIsDragActive(false)}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ')
              fileInputRef.current?.click();
          }}
        >
          {UPLOAD_ILLUSTRATION_SVG(classes.dropZoneIconSvg)}
          <span className={classes.dropText}>
            Drag and drop or{' '}
            <span
              className={classes.chooseFilesLink}
              role="button"
              tabIndex={0}
              onClick={e => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }
              }}
            >
              choose files
            </span>
          </span>
          <span className={classes.dropSubtext}>
            .pdf, .ppt, .docx, .txt, .csv
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_KB_FILE_EXTENSIONS}
            multiple
            hidden
            onChange={e => {
              if (e.target.files?.length) handleFiles(e.target.files);
              // eslint-disable-next-line no-param-reassign
              e.target.value = '';
            }}
          />
        </div>

        {/* Selected files */}
        {selectedFiles.map(file => (
          <div key={file.name} className={classes.selectedFileRow}>
            {FILE_ICON_SVG}
            <span className={classes.selectedFileName}>{file.name}</span>
            <button
              type="button"
              className={classes.removeFile}
              onClick={e => {
                e.stopPropagation();
                removeFile(file.name);
              }}
            >
              ✕
            </button>
          </div>
        ))}

        {/* Buttons side by side, same size */}
        <div className={classes.buttonRow}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.actionBtn}
            onClick={() => void handleUploadSubmit()}
            disabled={isUploading || selectedFiles.length === 0}
            startIcon={
              isUploading ? (
                <CircularProgress
                  size={14}
                  className={classes.uploadSubmitBtnSpinner}
                />
              ) : undefined
            }
          >
            {isUploading
              ? intl.formatMessage(messages.uploadingFile)
              : intl.formatMessage(messages.uploadFiles)}
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={classes.actionBtn}
            onClick={onSkip}
            disabled={isUploading}
          >
            {intl.formatMessage(messages.skipUpload)}
          </Button>
        </div>
      </div>
    </div>
  );
};
